import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/services/api';
import { useTableUpdates } from '@/hooks/useWebSocket';
import { useHasPermission } from '@/hooks/usePermission';
import { PERM } from '@/constants/permissions';
import TableGrid from '@/components/TableGrid';
import TableStatusPicker from '@/components/tables/TableStatusPicker';
import SearchBar from '@/components/SearchBar';
import FilterDropdown from '@/components/FilterDropdown';
import type { TableInfo, TableUpdateMessage } from '@/types/api';
import { X, AlertTriangle, RefreshCw } from 'lucide-react';

interface TablesTabProps {
  restaurantId: number;
}

export function TablesTab({ restaurantId }: TablesTabProps) {
  const queryClient = useQueryClient();

  const [selectedTable, setSelectedTable] = useState<TableInfo | null>(null);
  const [pickerTable, setPickerTable] = useState<TableInfo | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusChangeError, setStatusChangeError] = useState<string | null>(null);
  const [isSavingStatus, setIsSavingStatus] = useState(false);

  // UI gate: only managers with table.monitor may change a table's status.
  // The backend @PreAuthorize remains the source of truth.
  const canChangeStatus = useHasPermission(PERM.TABLE_MONITOR);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sectionFilter, setSectionFilter] = useState<string>('all');

  // Fetch tables
  const { data: tables = [], isLoading, isError, dataUpdatedAt, refetch } = useQuery({
    queryKey: ['tables', restaurantId],
    queryFn: () => apiClient.getTables(restaurantId),
    enabled: !!restaurantId,
  });

  // WebSocket real-time updates.
  // The POS sends table ids (posTableId) and raw status flags that don't line up with the
  // management-side row id / derived status used here, so invalidate and refetch the
  // authoritative list rather than patching the cache by a mismatched id.
  //
  // Debounced to one invalidation per ~1s window - a busy service can send a burst of
  // per-table updates, and invalidating on every single message would fire a refetch per
  // message instead of one refetch for the whole burst.
  const invalidateTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleTableUpdate = useCallback(
    (_message: TableUpdateMessage) => {
      if (invalidateTimeoutRef.current) return;
      invalidateTimeoutRef.current = setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['tables', restaurantId] });
        invalidateTimeoutRef.current = null;
      }, 1000);
    },
    [queryClient, restaurantId]
  );

  useEffect(() => {
    return () => {
      if (invalidateTimeoutRef.current) clearTimeout(invalidateTimeoutRef.current);
    };
  }, []);

  const { isConnected } = useTableUpdates(
    restaurantId.toString(),
    handleTableUpdate
  );

  // Extract unique sections for filter
  const availableSections = useMemo(() => {
    if (!tables) return [];
    const sections = Array.from(new Set(tables.map((t) => t.section || 'Unassigned'))).sort();
    return sections;
  }, [tables]);

  // Filter tables based on search and filters
  const filteredTables = useMemo(() => {
    if (!tables) return [];

    return tables.filter((table: TableInfo) => {
      const matchesSearch =
        searchQuery === '' ||
        table.tableNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (table.serverName &&
          table.serverName.toLowerCase().includes(searchQuery.toLowerCase()));

      const matchesStatus =
        statusFilter === 'all' || table.status === statusFilter;

      const matchesSection =
        sectionFilter === 'all' || (table.section || 'Main') === sectionFilter;

      return matchesSearch && matchesStatus && matchesSection;
    });
  }, [tables, searchQuery, statusFilter, sectionFilter]);

  const handleTableClick = (table: TableInfo) => {
    // Clicking a card always opens the read-only detail modal for everyone.
    // Managers can open the status picker from inside that modal.
    setSelectedTable(table);
    setStatusChangeError(null);
  };

  const closeModal = () => {
    // Ignore while a status change is in flight - closing wouldn't cancel the
    // request, but the user should see whether it succeeded before the modal
    // disappears out from under them.
    if (isSavingStatus) return;
    setSelectedTable(null);
    setStatusChangeError(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading tables...</div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3 text-center">
        <AlertTriangle className="h-8 w-8 text-red-500" />
        <div className="text-gray-900 font-medium">Failed to load tables</div>
        <button onClick={() => refetch()} className="btn btn-outline h-10">
          <RefreshCw className="h-4 w-4 mr-2" />
          Retry
        </button>
      </div>
    );
  }

  return (
    <>
      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <div
            className={`w-2 h-2 rounded-full ${
              isConnected ? 'bg-green-500' : 'bg-red-500'
            } animate-pulse`}
          />
          <span className="text-sm text-gray-600">
            {isConnected ? 'Live' : 'Offline'}
          </span>
        </div>
        <span className="text-xs text-gray-400">
          {isConnected ? 'Live-updating' : 'Last synced'} {new Date(dataUpdatedAt).toLocaleTimeString()}
        </span>
      </div>

      {/* Search and Filters */}
      <div className="card p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-2">
            <SearchBar
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Search by table number or server..."
            />
          </div>
          <FilterDropdown
            label="Status"
            value={statusFilter}
            onChange={setStatusFilter}
            // table.status is derived server-side from isOccupied/isBooked booleans
            // (TableMapper.deriveStatus) and can only ever be one of these three -
            // CLEANING/OUT_OF_SERVICE are never produced, so they were dead options
            // that could never match a table. The exact POS status (e.g. a custom
            // "Settlement"/"Bill Requested" state) is a separate field, statusName,
            // not filterable here.
            options={[
              { label: 'All Tables', value: 'all' },
              { label: 'Available', value: 'AVAILABLE' },
              { label: 'Occupied', value: 'OCCUPIED' },
              { label: 'Reserved', value: 'RESERVED' },
            ]}
          />
          <FilterDropdown
            label="Floor"
            value={sectionFilter}
            onChange={setSectionFilter}
            options={[
              { label: 'All Floors', value: 'all' },
              ...availableSections.map((section) => ({
                label: section,
                value: section,
              })),
            ]}
          />
        </div>

        {/* Results count */}
        <div className="mt-3 text-sm text-gray-600">
          Showing {filteredTables.length} of {tables.length} tables
        </div>
      </div>

      {/* Table Grid */}
      <TableGrid tables={filteredTables} onTableClick={handleTableClick} />

      {/* Table Detail Modal (read-only) */}
      {selectedTable && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={closeModal}
        >
          <div
            className="card max-w-lg w-full mx-4 p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-xl font-bold text-gray-900">
                  Table {selectedTable.tableNumber}
                </h3>
                <p className="text-sm text-gray-500">
                  {selectedTable.section === 'Unassigned' ? 'Unassigned' : `${selectedTable.section} Floor`}
                </p>
              </div>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md p-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
                aria-label="Close modal"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label htmlFor="table-detail-status" className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <div className="text-lg font-semibold">
                  {selectedTable.statusName || selectedTable.status}
                </div>
                {canChangeStatus && (
                  <div className="mt-2 space-y-2">
                    {/* Coarse availability (AVAILABLE/OCCUPIED/RESERVED) */}
                    <select
                      id="table-detail-status"
                      className="input w-full"
                      value={selectedTable.status}
                      disabled={isSavingStatus}
                      onChange={async (e) => {
                        const next = e.target.value as 'AVAILABLE' | 'OCCUPIED' | 'RESERVED';
                        setStatusChangeError(null);
                        setIsSavingStatus(true);
                        try {
                          await apiClient.updateTableStatus(restaurantId, selectedTable.id, next);
                          queryClient.invalidateQueries({ queryKey: ['tables', restaurantId] });
                          setSelectedTable({ ...selectedTable, status: next as TableInfo['status'] });
                        } catch (error: any) {
                          setStatusChangeError(error.response?.data?.message || 'Failed to update status');
                        } finally {
                          setIsSavingStatus(false);
                        }
                      }}
                    >
                      <option value="AVAILABLE">Available</option>
                      <option value="OCCUPIED">Occupied</option>
                      <option value="RESERVED">Reserved</option>
                    </select>
                    {statusChangeError && (
                      <p className="text-red-600 text-sm">{statusChangeError}</p>
                    )}
                    {/* Exact POS TableStatus (Free/Open/Bill/Cleaning…) */}
                    <button
                      type="button"
                      onClick={() => setPickerTable(selectedTable)}
                      className="btn btn-outline h-10 w-full"
                    >
                      Change exact status…
                    </button>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Capacity
                  </label>
                  <div className="text-lg font-semibold">
                    {selectedTable.capacity}
                  </div>
                </div>

                {selectedTable.covers !== undefined && selectedTable.covers > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Current Covers
                    </label>
                    <div className="text-lg font-semibold">
                      {selectedTable.covers}
                    </div>
                  </div>
                )}
              </div>

              {selectedTable.serverName && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Server
                  </label>
                  <div className="text-lg">{selectedTable.serverName}</div>
                </div>
              )}

              {selectedTable.currentTicketId && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ticket ID
                  </label>
                  <div className="text-lg font-mono">
                    {selectedTable.currentTicketId}
                  </div>
                </div>
              )}

              <div className="pt-4 border-t border-gray-200">
                <button
                  onClick={closeModal}
                  className="btn btn-outline w-full h-10"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Status picker (managers only), opened from the detail modal.
          Rendered last so it paints above the detail modal. */}
      {pickerTable && (
        <TableStatusPicker
          restaurantId={restaurantId}
          table={pickerTable}
          onClose={() => setPickerTable(null)}
        />
      )}
    </>
  );
}

export default TablesTab;
